import React, { ReactElement, useMemo } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { formulaSheetEntriesAtom } from '@/atoms/formulaSheetEntries'
import { getFormulasForLeafNode } from 'common/src/formulaSheet/utils'
import { Button, Empty } from 'antd'
import { FunctionOutlined } from '@ant-design/icons'
import styles from './LeafNodeFormulaList.module.less'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import { unHashContentPath } from 'common/src/ContentPath'

export interface LeafNodeFormulaListProps {
    hashedContentPath: string
}

export const LeafNodeFormulaList: React.FC<LeafNodeFormulaListProps> = (
    props
): ReactElement => {
    const formulaSheetEntries = useRecoilValue(formulaSheetEntriesAtom)
    const setViewingState = useSetRecoilState(viewingStateAtom)
    const formulasForThisNode = useMemo(() => {
        return getFormulasForLeafNode(
            formulaSheetEntries,
            props.hashedContentPath
        )
    }, [formulaSheetEntries, props.hashedContentPath])
    const openFormulaSheet = (): void =>
        setViewingState({
            contentPath: unHashContentPath(props.hashedContentPath),
            viewingStateType: ViewingStateType.formulaSheet,
            id: null,
        })
    const header = (
        <div className={styles.header}>
            <FunctionOutlined className={styles.icon} />
            Formula Sheet Entries for This Topic
            {formulasForThisNode.length > 0 &&
                ` (${formulasForThisNode.length})`}
            <Button type={'link'} size={'small'} onClick={openFormulaSheet}>
                Open formula sheet
            </Button>
        </div>
    )

    if (formulasForThisNode.length === 0) {
        return (
            <div className={styles.leafNodeFormulaList}>
                {header}
                <Empty
                    description="No formulas linked to this topic yet"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </div>
        )
    }

    return (
        <div className={styles.leafNodeFormulaList}>
            {header}
            <div className={styles.formulaList}>
                {formulasForThisNode.map((formula) => (
                    <div key={formula.id} className={styles.formulaItem}>
                        <div className={styles.formulaTitle}>
                            {formula.title}
                        </div>
                        {formula.description && (
                            <div className={styles.formulaDescription}>
                                {formula.description}
                            </div>
                        )}
                        <Button
                            type={'link'}
                            size={'small'}
                            onClick={() => {
                                setViewingState({
                                    contentPath: [],
                                    viewingStateType:
                                        ViewingStateType.formulaSheet,
                                    id: formula.id,
                                })
                            }}
                        >
                            View in Formula Sheet Editor
                        </Button>
                    </div>
                ))}
            </div>
        </div>
    )
}
